// Peças da área de Trackeamento usadas em mais de uma tela.
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Check, CheckCircle2, Circle, Copy, LayoutTemplate, Lock } from 'lucide-react'
import type { LandingAllowanceDto, TrackingChecklistItem, TrackingStatus } from '@impulso/shared/api/tracking'
import { errorMessage } from '../../lib/api'
import { useConfirm } from '../../ui/confirm'
import { useToast } from '../../ui/toast'
import { money } from '../billing/api'
import type { PlatformGuide } from './guides'
import { useSetLandingAddon } from './api'
import './tracking.css'

// `code`: trecho de código sem quebra automática de linha (rola para os lados em vez de cortar o texto)
export function CopyBox({ value, label, rows, code }: { value: string; label: string; rows?: number; code?: boolean }) {
  const toast = useToast()
  const [copied, setCopied] = useState(false)
  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast('Copiado!')
    } catch {
      toast('Não foi possível copiar. Selecione o texto e copie manualmente.')
    }
  }
  return (
    <div className="copy-box">
      <textarea readOnly aria-label={label} value={value} wrap={code ? 'off' : undefined} className={code ? 'nowrap' : undefined} rows={rows ?? Math.min(8, value.split('\n').length + 1)} onFocus={(e) => e.currentTarget.select()} />
      {code && <span className="small muted copy-hint">Arraste para os lados para ver o código inteiro.</span>}
      <button type="button" className="btn primary sm" onClick={() => void copy()}>{copied ? <><Check size={14} /> Copiado</> : <><Copy size={14} /> Copiar código</>}</button>
    </div>
  )
}

const STATUS: Record<TrackingStatus, { cls: string; dot: string; text: string }> = {
  not_started: { cls: '', dot: '#9ca3af', text: 'Não configurado' },
  waiting: { cls: 'amber', dot: '#d97706', text: 'Aguardando o primeiro sinal' },
  receiving: { cls: 'green', dot: '#16a34a', text: 'Recebendo dados' },
  stale: { cls: 'red', dot: '#dc2626', text: 'Sem sinais há mais de 7 dias' },
}

export function TrackingStatusBadge({ status }: { status: TrackingStatus }) {
  const s = STATUS[status]
  return <span className={`badge ${s.cls}`} style={{ alignSelf: 'flex-start' }}><span className={`status-dot ${status === 'receiving' ? 'pulse' : ''}`} style={{ background: s.dot }} />{s.text}</span>
}

export function Checklist({ items, companyId }: { items: TrackingChecklistItem[]; companyId: string }) {
  const done = items.filter((i) => i.done).length
  return (
    <div className="card">
      <div className="row between" style={{ gap: 12 }}>
        <h2>Passo a passo</h2>
        <span className="small muted">{done} de {items.length} concluídos</span>
      </div>
      <div className="progress mt-12"><span style={{ width: `${Math.round((done / items.length) * 100)}%` }} /></div>
      <ol className="checklist mt-16">
        {items.map((i) => (
          <li key={i.id} className={i.done ? 'done' : ''}>
            {i.done ? <CheckCircle2 size={22} color="var(--green)" /> : <Circle size={22} color="var(--border-strong)" />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="strong">{i.title}</div>
              <div className="small muted">{i.description}</div>
            </div>
            {!i.done && <Link className="btn sm" to={`/app/${companyId}/${i.to}`}>Fazer agora</Link>}
          </li>
        ))}
      </ol>
    </div>
  )
}

export function PlatformMark({ guide, size = 36 }: { guide: Pick<PlatformGuide, 'mark' | 'color'>; size?: number }) {
  return <span className="pf-mark" style={{ background: guide.color, width: size, height: size, fontSize: guide.mark.length > 2 ? size * 0.3 : size * 0.4 }}>{guide.mark}</span>
}

/** Oferta/ativação do adicional de landing pages. */
export function LandingAddonCard({ allowance, compact }: { allowance: LandingAllowanceDto; compact?: boolean }) {
  const toast = useToast()
  const confirm = useConfirm()
  const set = useSetLandingAddon(allowance.accountId)
  if (allowance.active) return null
  const activate = async () => {
    if (!(await confirm({ title: `Ativar o adicional de Páginas por ${money(allowance.price)}/mês?`, message: 'O valor entra junto com a sua assinatura.', confirmLabel: 'Ativar' }))) return
    set.mutate(true, { onSuccess: () => toast('Adicional ativado. Agora você pode publicar suas páginas.'), onError: (e) => toast(errorMessage(e)) })
  }
  return (
    <div className={`card addon-card ${compact ? 'compact' : ''}`}>
      <span className="ic purple"><LayoutTemplate size={20} /></span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row wrap" style={{ gap: 8 }}><h3>Páginas</h3><span className="badge purple">Adicional</span></div>
        {!compact && (
          <ul className="addon-list small">
            <li>Editor completo: blocos prontos, cores, fotos, formulário e botão de WhatsApp</li>
            <li>Medição já instalada: Pixel da Meta, tag do Google e cliques no WhatsApp</li>
            <li>Contatos do formulário chegam aqui e no seu e-mail</li>
            <li>Monte e veja a prévia à vontade; o adicional é necessário para publicar</li>
          </ul>
        )}
        <div className="mt-8"><b style={{ fontSize: 18 }}>+ {money(allowance.price)}</b><span className="muted">/mês</span></div>
      </div>
      {allowance.canManageBilling
        ? <button className="btn primary" disabled={set.isPending} onClick={activate}>{set.isPending ? 'Ativando…' : 'Ativar adicional'}</button>
        : <span className="small muted row" style={{ gap: 6, maxWidth: 220 }}><Lock size={14} /> Peça ao dono da conta para ativar em Plano e cobrança.</span>}
    </div>
  )
}

export function fmtDateTime(iso: string | null) {
  return iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'
}

export function relativeTime(iso: string | null): string {
  if (!iso) return 'nunca'
  const diff = Date.now() - new Date(iso).getTime()
  const min = Math.round(diff / 60000)
  if (min < 1) return 'agora há pouco'
  if (min < 60) return `há ${min} min`
  const h = Math.round(min / 60)
  if (h < 24) return `há ${h} h`
  const d = Math.round(h / 24)
  return `há ${d} dia${d > 1 ? 's' : ''}`
}
